import React, { useEffect, useState } from "react";
import { Button } from "@component/button";
import { Progress } from "@component/progress";
import {
  CheckIcon,
  XIcon,
  ImageIcon,
  BriefcaseIcon,
  BookIcon,
  AwardIcon,
  FileTextIcon,
} from "lucide-react";
import { WorkPreferencesModal } from "@container/work-preferences-modal";
import { useNavigate } from "react-router-dom";
import { getProfileCompletion, jobProfileCompletion } from "@service/job";
import { ProfileCompletionItem } from "@type/job";

export function ProfileCompletion() {
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [completionItems, setCompletionItems] = useState<
    ProfileCompletionItem[]
  >([
    {
      name: "resume_upload",
      label: "Resume Upload",
      completed: false,
      message: null,
    },
    {
      name: "work_preferences",
      label: "Work Preferences",
      completed: false,
      message: null,
    },
    {
      name: "projects",
      label: "Projects",
      completed: false,
      message: null,
    },
    {
      name: "publications",
      label: "Publications",
      completed: false,
      message: null,
    },
    {
      name: "awards",
      label: "Awards",
      completed: false,
      message: null,
    },
    {
      name: "profile_photo",
      label: "Profile Photo",
      completed: false,
      message: null,
    },
  ]);

  const [isWorkPreferencesModalOpen, setIsWorkPreferencesModalOpen] =
    useState(false);

  async function fetchProfileData() {
    try {
      setIsLoading(true);
      setError(null);
      const data = await getProfileCompletion();
      // Update completion items based on API response
      if (data && data.items) {
        setCompletionItems((prevItems) =>
          prevItems.map((item) => {
            const apiItem = data.items.find(
              (apiItem) =>
                apiItem.name.toLowerCase() === item.name.toLowerCase()
            );
            return apiItem
              ? {
                ...item,
                message: apiItem.message,
                completed: apiItem.completed,
              }
              : item;
          })
        );
      }
    } catch (err) {
      console.error("Error fetching profile completion data:", err);
      setError("Failed to load profile completion data.");
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    fetchProfileData();
  }, []);

  const completedItems = completionItems.filter(
    (item) => item.completed
  ).length;
  const totalItems = completionItems.length;
  const completionPercentage = Math.floor((completedItems / totalItems) * 100);

  const getItemIcon = (itemName: string) => {
    switch (itemName) {
      case "resume_upload":
        return <FileTextIcon className="h-4 w-4" />;
      case "work_preferences":
        return <BriefcaseIcon className="h-4 w-4" />;
      case "projects":
        return <BookIcon className="h-4 w-4" />;
      case "publications":
        return <FileTextIcon className="h-4 w-4" />;
      case "awards":
        return <AwardIcon className="h-4 w-4" />;
      case "profile_photo":
        return <ImageIcon className="h-4 w-4" />;
      default:
        return null;
    }
  };

  const handleWorkPreferencesClick = () => {
    setIsWorkPreferencesModalOpen(true);
  };

  const onCloseWorkPreference = () => {
    setIsWorkPreferencesModalOpen(false);
  };

  const onSaveWorkPreference = () => {
    setCompletionItems((prevItems) =>
      prevItems.map((item) =>
        item.name === "work_preferences" ? { ...item, completed: true } : item
      )
    );
    setIsWorkPreferencesModalOpen(false);
  };

  const handleProfileCompletedByName = async (name: string) => {
    setCompletionItems((prevItems) =>
      prevItems.map((item) =>
        item.name === name ? { ...item, completed: true } : item
      )
    );

    try {
      await jobProfileCompletion({
        item: name,
        action: "skip",
      });
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      console.error("failed to completed profile, err", errorMessage);
    }
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6 mb-10">
        <div className="flex justify-center items-center h-40">
          <div className="animate-spin h-8 w-8 border-4 border-black border-t-transparent rounded-full"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6 mb-10">
        <div className="flex justify-center items-center h-40 flex-col">
          <XIcon className="h-10 w-10 text-red-500 mb-2" />
          <p className="text-red-500">{error}</p>
          <Button
            variant="outline"
            size="sm"
            className="mt-4"
            onClick={() => fetchProfileData()}
          >
            Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6 mb-10">
      <div className="flex justify-between items-start mb-4">
        <div>
          <h3 className="text-xl font-bold text-gray-900">
            Profile Completion
          </h3>
          <p className="text-sm text-lime-500">
            Complete your profile to improve job matches.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            className="text-xs bg-white text-black border-black"
            onClick={handleWorkPreferencesClick}
          >
            Work Preferences
          </Button>
          {completionPercentage !== 100 && (
            <Button
              variant="default"
              size="sm"
              onClick={() => navigate("/profile")}
            >
              Complete Profile
            </Button>
          )}
        </div>
      </div>

      <div className="flex items-center justify-between text-sm mb-3">
        <span className="text-gray-800">{completionPercentage}% Complete</span>
        <span className="text-gray-800">
          {completedItems}/{totalItems} items
        </span>
      </div>

      <Progress
        value={completionPercentage}
        className="h-2 mb-6"
        style={{
          backgroundColor: "#f3f4f6",
        }}
      >
        <div
          className="h-full bg-black"
          style={{
            width: `${completionPercentage}%`,
            transition: "width 0.3s ease",
          }}
        />
      </Progress>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3">
        {completionItems.map((item, index) => (
          <div key={index} className="flex flex-col" id={`httkcl_${index}`}>
            <div
              className="flex items-center justify-between"
              id={`vqm2vs_${index}`}
            >
              <div className="flex items-center gap-2" id={`4xgywi_${index}`}>
                {item.completed ? (
                  <CheckIcon
                    className="h-4 w-4 text-lime-500"
                    id={`kmg5x1_${index}`}
                  />
                ) : (
                  <XIcon
                    className="h-4 w-4 text-red-500"
                    id={`en6yx0_${index}`}
                  />
                )}

                <span
                  className={`text-sm flex items-center gap-1 ${item.completed ? "text-gray-900" : "text-gray-700"
                    }`}
                  id={`nlggcj_${index}`}
                >
                  {getItemIcon(item.name)}
                  {item.label}
                </span>
              </div>

              {!!item.message &&
                !item.completed &&
                item.name !== "work_preferences" && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-xs text-gray-600 hover:text-gray-900 hover:bg-gray-100 p-0 h-auto ml-2"
                    onClick={() => handleProfileCompletedByName(item.name)}
                  >
                    {item.message}
                  </Button>
                )}
            </div>
          </div>
        ))}
      </div>

      <p className="text-xs text-gray-600 mt-5">
        Your profile has been created based on your resume. You can edit your
        details accordingly.
      </p>

      {/* Work Preferences Modal */}
      <WorkPreferencesModal
        isOpen={isWorkPreferencesModalOpen}
        onClose={onCloseWorkPreference}
        onSave={onSaveWorkPreference}
      />
    </div>
  );
}
